import { motion } from "framer-motion";
import { ArrowRight, Anchor } from "lucide-react";
import { COMPANY, HERO_STATS, IMAGES } from "@/data/site";
import { scrollToId } from "@/lib/scroll";

const EASE = [0.22, 1, 0.36, 1];

const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100svh] flex items-center overflow-hidden bg-navy-900"
    >
      <img
        src={IMAGES.hero}
        alt="Commercial cargo vessel navigating open ocean"
        className="absolute inset-0 h-full w-full object-cover"
        fetchPriority="high"
      />
      <div className="absolute inset-0 eb-hero-overlay" />
      <div className="absolute inset-0 eb-grain" />

      <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8 w-full pt-28 pb-16">
        <div className="max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE }}
            className="flex items-center gap-3 text-white/70"
          >
            <Anchor size={15} className="text-brand-blue" />
            <span className="eb-overline">{COMPANY.name} · {COMPANY.tagline}</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.08, ease: EASE }}
            className="mt-6 text-4xl sm:text-5xl lg:text-[4.2rem] leading-[1.02] tracking-tight font-black text-white"
          >
            Strategic advisory for the world's
            <span className="text-silver-300"> maritime fleets.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.18, ease: EASE }}
            className="mt-7 max-w-xl text-base sm:text-lg leading-relaxed text-silver-300/90 font-medium"
          >
            Echo Bravo partners with Shipowners, Operators and Investors to
            optimise fleet performance, navigate complex regulation and de-risk
            marine operations across global trade routes.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.28, ease: EASE }}
            className="mt-9 flex flex-col sm:flex-row gap-3"
          >
            <button
              data-testid="hero-cta-button"
              onClick={() => scrollToId("#contact")}
              className="group inline-flex items-center justify-center gap-2 rounded-sm bg-brand-blue px-7 py-4 text-sm font-bold text-white transition-all duration-300 hover:bg-white hover:text-navy-900 hover:-translate-y-0.5"
            >
              Request a Consultation
              <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-secondary-button"
              onClick={() => scrollToId("#services")}
              className="inline-flex items-center justify-center rounded-sm border border-white/25 px-7 py-4 text-sm font-bold text-white transition-all duration-300 hover:bg-white/10"
            >
              Explore Our Services
            </button>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4, ease: EASE }}
          className="mt-16 sm:mt-20 grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 max-w-4xl"
        >
          {HERO_STATS.map((s) => (
            <div key={s.label} className="bg-navy-900/40 backdrop-blur-sm px-6 py-6">
              <div className="text-3xl font-black text-white tracking-tight">{s.value}</div>
              <div className="mt-1 text-xs uppercase tracking-[0.15em] text-silver-400 font-semibold">
                {s.label}
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
